import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ServeOrderForm } from './ServeOrderForm';
import { PrepareOrderForm } from './PrepareOrderForm';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ColorBadge } from "@/components/ColorBadge";
import { ProductSelector } from "@/components/ProductSelector";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { X, Eye, Package, Truck, Filter, Search } from "lucide-react";
import { fetchSeasonDatePresets, toDateRangeFromPreset } from "@/lib/seasonDatePresets";

const PICKING_ORDER_STATUSES = ['pending', 'partially_served', 'partially_prepared'];

export function PendingOrders({ onOrderServed }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ step: '', percentage: 0 });
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showServeDialog, setShowServeDialog] = useState(false);
  const [showPrepareDialog, setShowPrepareDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showOnlyWithStock, setShowOnlyWithStock] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userCodRep, setUserCodRep] = useState(null);
  const [canViewAllOrders, setCanViewAllOrders] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20,
    hasMore: true
  });
  const [seasonDatePresets, setSeasonDatePresets] = useState([]);
  
  // Filtros temporales (no aplicados hasta que se presione "Aplicar filtros")
  const [tempFilters, setTempFilters] = useState({
    customer: "",
    orderNumber: "",
    deliveryDateRange: { from: null, to: null },
    temporada: "all",
    minServablePercentage: 0,
    preparationStatus: "all",
    product: null,
  });

  // Estado para el filtro de cliente con debounce
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [debouncedCustomerTerm, setDebouncedCustomerTerm] = useState("");
  
  // Estado para el filtro de número de pedido
  const [orderNumberSearchTerm, setOrderNumberSearchTerm] = useState("");
  const [debouncedOrderNumberTerm, setDebouncedOrderNumberTerm] = useState("");
  
  // Filtros aplicados (los que realmente se usan en la consulta)
  const [appliedFilters, setAppliedFilters] = useState({
    customer: "",
    orderNumber: "",
    deliveryDateRange: { from: null, to: null },
    temporada: "all",
    minServablePercentage: 0,
    preparationStatus: "all",
    product: null,
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchUserRole();
    loadSeasonDatePresets();
    fetchPendingOrders(0, false);
  }, []); // Solo ejecutar al montar el componente

  // Debounce para el filtro de cliente (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerTerm(customerSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearchTerm]);

  // Debounce para el filtro de número de pedido (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOrderNumberTerm(orderNumberSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [orderNumberSearchTerm]);

  // Sincronizar los términos debounced con tempFilters
  useEffect(() => {
    setTempFilters(prev => ({ 
      ...prev, 
      customer: debouncedCustomerTerm,
      orderNumber: debouncedOrderNumberTerm
    }));
  }, [debouncedCustomerTerm, debouncedOrderNumberTerm]);

  const fetchUserRole = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;

      if (!user) {
        throw new Error('No hay usuario autenticado');
      }

      const { data: userData, error: roleError } = await supabase
        .from('users')
        .select('role, cod_rep, ver_comisiones_globales')
        .eq('id', user.id)
        .single();

      if (roleError) throw roleError;
      setUserRole(userData?.role);
      setUserCodRep(userData?.cod_rep);
      setCanViewAllOrders(userData?.ver_comisiones_globales || userData?.role === 'admin');
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const loadSeasonDatePresets = async () => {
    const presets = await fetchSeasonDatePresets();
    setSeasonDatePresets((presets || []).filter((preset) => preset.active !== false));
  };

  const filterOutCancelledPickingOrders = async (ordersData) => {
    if (!ordersData || ordersData.length === 0) return [];

    const orderIds = ordersData.map((order) => order.id);
    const { data: cancelledLinks, error } = await supabase
      .from('cancelled_orders')
      .select('original_order_id, cancellation_reason, status')
      .in('original_order_id', orderIds);

    if (error) throw error;

    const excludedOrderIds = new Set(
      (cancelledLinks || [])
        .filter((row) =>
          row.cancellation_reason === 'partial_served_not_manage' ||
          row.status === 'partially_served_cancelled'
        )
        .map((row) => row.original_order_id)
        .filter(Boolean)
    );

    return ordersData.filter((order) => !excludedOrderIds.has(order.id));
  };

  const fetchAllOrders = async () => {
    try {
      setLoadingProgress({ step: 'Cargando pedidos...', percentage: 10 });
      
      // Cargar directamente los pedidos que tienen items usando una consulta más eficiente
      let ordersQuery = supabase
        .from('orders')
        .select(`
          id,
          number,
          date,
          status,
          preparation_status,
          total,
          fecha_entrega,
          customer:customers(
            id,
            name,
            cod_rep
          ),
          order_items(
            id,
            quantity,
            pending_quantity,
            delivered_quantity,
            preparation_quantity,
            preparation_status,
            product_id,
            size,
            color
          )
        `)
        .in('status', PICKING_ORDER_STATUSES)
        .order('number', { ascending: false });

      // Aplicar filtros de permisos de usuario
      if (userRole === 'vendedor' && !canViewAllOrders && userCodRep) {
        ordersQuery = ordersQuery.eq('customer.cod_rep', userCodRep);
      }

      const { data: ordersData, error: ordersError } = await ordersQuery;

      if (ordersError) throw ordersError;
      const visibleOrdersData = await filterOutCancelledPickingOrders(ordersData || []);
      
      if (visibleOrdersData.length === 0) {
        setOrders([]);
        setPagination(prev => ({ ...prev, hasMore: false }));
        return;
      }

      setLoadingProgress({ step: 'Procesando datos...', percentage: 30 });

      // Filtrar solo pedidos que tienen items (no filtrar por pending_quantity aquí)
      const ordersWithItems = visibleOrdersData.filter(order => order.order_items && order.order_items.length > 0);

      if (ordersWithItems.length === 0) {
        setOrders([]);
        setPagination(prev => ({ ...prev, hasMore: false }));
        return;
      }

      // Extraer todos los items para cargar productos e inventario
      const allItems = ordersWithItems.flatMap(order => order.order_items);
      setLoadingProgress({ step: 'Cargando productos...', percentage: 50 });

      // Extraer IDs de productos para cargar datos relacionados
      const productIds = [...new Set(allItems.map(item => item.product_id))];
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`
          id,
          code,
          description,
          images,
          main_image_index,
          temporada
        `)
        .in('id', productIds);

      if (productsError) throw productsError;

      const { data: inventoryData, error: inventoryError } = await supabase
        .from('inventory')
        .select(`
          id,
          product_id,
          quantity,
          size,
          color
        `)
        .in('product_id', productIds);

      if (inventoryError) throw inventoryError;

      setLoadingProgress({ step: 'Procesando inventario...', percentage: 70 });

      const productsMap = new Map(productsData.map(product => [product.id, product]));
      const inventoryMap = new Map();
      inventoryData.forEach(inv => {
        const key = `${inv.product_id}-${inv.size}-${inv.color}`;
        inventoryMap.set(key, inv);
      });

      // Procesar items con datos de productos e inventario
      const itemsByOrder = new Map();
      const allItemsByOrder = new Map();
      
      ordersWithItems.forEach(order => {
        const orderItems = order.order_items.map(item => {
          const product = productsMap.get(item.product_id);
          const inventoryKey = `${item.product_id}-${item.size}-${item.color}`;
          const inventoryItem = inventoryMap.get(inventoryKey);
          
          return {
            ...item,
            product: {
              ...product,
              inventory: inventoryItem ? [inventoryItem] : []
            }
          };
        });

        // Agrupar todos los items (para cálculos)
        allItemsByOrder.set(order.id, orderItems);

        // Agrupar solo items pendientes (para mostrar)
        const pendingItems = orderItems.filter(item => item.pending_quantity > 0);
        if (pendingItems.length > 0) {
          itemsByOrder.set(order.id, pendingItems);
        }
      });

      const allOrders = ordersWithItems
        .map(order => ({
          ...order,
          order_items: itemsByOrder.get(order.id) || [], // Items pendientes para mostrar
          all_order_items: allItemsByOrder.get(order.id) || [] // Todos los items para cálculos
        }));

      setLoadingProgress({ step: 'Finalizando...', percentage: 90 });
      
      // Guardar todos los pedidos en el estado
      setOrders(allOrders);
      setPagination(prev => ({ ...prev, page: 0, hasMore: false })); // No paginación cuando se cargan todos

    } catch (error) {
      console.error('Error fetching all orders:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los pedidos",
        variant: "destructive"
      });
    } finally {
      setLoadingProgress({ step: '', percentage: 0 });
      setLoading(false);
    }
  };




  const fetchPendingOrders = async (page = 0, append = false) => {
    try {
      if (!append) setLoading(true);
      
      // Siempre cargar todos los pedidos para evitar problemas con filtros
      if (!append) {
        await fetchAllOrders();
        return;
      }
      
      // Para cargas adicionales (append), usar paginación
      const pageSize = pagination.pageSize;
      const from = page * pageSize;
      const to = from + pageSize - 1;
      
      // Construir la consulta base
      let query = supabase
        .from('orders')
        .select(`
          id,
          number,
          date,
          status,
          preparation_status,
          total,
          fecha_entrega,
          customer:customers(
            id,
            name,
            cod_rep
          )
        `)
        .in('status', PICKING_ORDER_STATUSES)
        .order('number', { ascending: false });

      // Aplicar filtros en la base de datos
      if (appliedFilters.customer) {
        // Primero obtener los IDs de clientes que coincidan
        const { data: customerIds, error: customerError } = await supabase
          .from('customers')
          .select('id')
          .ilike('name', `%${appliedFilters.customer}%`);

        if (customerError) throw customerError;

        if (customerIds && customerIds.length > 0) {
          const ids = customerIds.map(c => c.id);
          query = query.in('customer_id', ids);
        } else {
          // Si no hay clientes que coincidan, devolver resultados vacíos
          setPagination(prev => ({ ...prev, hasMore: false }));
          if (!append) setOrders([]);
          return;
        }
      }

      if (appliedFilters.deliveryDateRange.from) {
        // Convertir la fecha a formato YYYY-MM-DD para comparación con campo date
        const fromDate = appliedFilters.deliveryDateRange.from.toISOString().split('T')[0];
        query = query.not('fecha_entrega', 'is', null).gte('fecha_entrega', fromDate);
      }

      if (appliedFilters.deliveryDateRange.to) {
        // Convertir la fecha a formato YYYY-MM-DD para comparación con campo date
        const toDate = appliedFilters.deliveryDateRange.to.toISOString().split('T')[0];
        query = query.not('fecha_entrega', 'is', null).lte('fecha_entrega', toDate);
      }

      // Si se aplica cualquier filtro de fecha, excluir pedidos sin fecha de entrega
      if (appliedFilters.deliveryDateRange.from || appliedFilters.deliveryDateRange.to) {
        query = query.not('fecha_entrega', 'is', null);
      }

      // Filtrar por cod_rep si es necesario
      if (userRole !== 'admin' && !canViewAllOrders && userCodRep) {
        // Primero obtener los IDs de clientes con ese cod_rep
        const { data: repCustomerIds, error: repError } = await supabase
          .from('customers')
          .select('id')
          .eq('cod_rep', userCodRep);

        if (repError) throw repError;

        if (repCustomerIds && repCustomerIds.length > 0) {
          const ids = repCustomerIds.map(c => c.id);
          query = query.in('customer_id', ids);
        } else {
          // Si no hay clientes con ese cod_rep, devolver resultados vacíos
          setPagination(prev => ({ ...prev, hasMore: false }));
          if (!append) setOrders([]);
          return;
        }
      }

      // Aplicar paginación
      query = query.range(from, to);

      const { data: ordersData, error: ordersError } = await query;

      if (ordersError) throw ordersError;
      const visibleOrdersData = await filterOutCancelledPickingOrders(ordersData || []);

      // Si no hay pedidos, actualizar paginación
      if (visibleOrdersData.length === 0) {
        setPagination(prev => ({ ...prev, hasMore: false }));
        if (!append) setOrders([]);
        return;
      }

      // Segunda consulta: obtener TODOS los items de pedidos por lotes (no solo los pendientes)
      const orderIds = visibleOrdersData.map(order => order.id);
      const { data: orderItemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          id,
          order_id,
          quantity,
          pending_quantity,
          delivered_quantity,
          preparation_quantity,
          preparation_status,
          product_id,
          size,
          color
        `)
        .in('order_id', orderIds);



      if (itemsError) throw itemsError;

      // Filtrar por producto si está aplicado
      let filteredItems = orderItemsData;
      if (appliedFilters.product) {
        filteredItems = orderItemsData.filter(item => item.product_id === appliedFilters.product);
        // Si no hay items después del filtro, no hay pedidos que mostrar
        if (filteredItems.length === 0) {
          setPagination(prev => ({ ...prev, hasMore: false }));
          if (!append) setOrders([]);
          return;
        }
      }

      // Separar items pendientes (para mostrar) de todos los items (para cálculos)
      const pendingItems = orderItemsData.filter(item => item.pending_quantity > 0);
      const allItems = orderItemsData; // Todos los items para cálculos

      // Tercera consulta: obtener productos por lotes
      const productIds = [...new Set(orderItemsData.map(item => item.product_id))];
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`
          id,
          code,
          description,
          images,
          main_image_index,
          temporada
        `)
        .in('id', productIds);

      if (productsError) throw productsError;

      // Cuarta consulta: obtener inventario por lotes
      const { data: inventoryData, error: inventoryError } = await supabase
        .from('inventory')
        .select(`
          id,
          product_id,
          quantity,
          size,
          color
        `)
        .in('product_id', productIds);

      if (inventoryError) throw inventoryError;

      // Combinar los datos
      const productsMap = new Map(productsData.map(product => [product.id, product]));
      const inventoryMap = new Map();
      inventoryData.forEach(inv => {
        const key = `${inv.product_id}-${inv.size}-${inv.color}`;
        inventoryMap.set(key, inv);
      });

      // Agrupar items por pedido
      const itemsByOrder = new Map();
      const allItemsByOrder = new Map();
      
      orderItemsData.forEach(item => {
        const product = productsMap.get(item.product_id);
        const inventoryKey = `${item.product_id}-${item.size}-${item.color}`;
        const inventoryItem = inventoryMap.get(inventoryKey);
        
        const itemWithProduct = {
          ...item,
          product: {
            ...product,
            inventory: inventoryItem ? [inventoryItem] : []
          }
        };

        // Agrupar todos los items (para cálculos)
        if (!allItemsByOrder.has(item.order_id)) {
          allItemsByOrder.set(item.order_id, []);
        }
        allItemsByOrder.get(item.order_id).push(itemWithProduct);

        // Agrupar solo items pendientes (para mostrar)
        if (item.pending_quantity > 0) {
          if (!itemsByOrder.has(item.order_id)) {
            itemsByOrder.set(item.order_id, []);
          }
          itemsByOrder.get(item.order_id).push(itemWithProduct);
        }
      });

      // Combinar todo
      const pendingOrders = visibleOrdersData
        .map(order => ({
          ...order,
          order_items: itemsByOrder.get(order.id) || [], // Items pendientes para mostrar
          all_order_items: allItemsByOrder.get(order.id) || [] // Todos los items para cálculos
        }))
        .filter(order => order.order_items.length > 0);

      // Aplicar filtros adicionales que requieren cálculos
      const finalOrders = pendingOrders.filter(order => {
        // Filtro por porcentaje servible mínimo
        if (appliedFilters.minServablePercentage > 0) {
          const servablePercentage = calculateServablePercentage(order);
          if (servablePercentage < appliedFilters.minServablePercentage) {
            return false;
          }
        }

        // Filtro de estado de preparación
        if (appliedFilters.preparationStatus !== "all") {
          const preparationPercentage = calculatePreparationPercentage(order);
          
          if (appliedFilters.preparationStatus === "prepared" && preparationPercentage < 100) {
            return false;
          }
          if (appliedFilters.preparationStatus === "partial" && (preparationPercentage === 0 || preparationPercentage === 100)) {
            return false;
          }
          if (appliedFilters.preparationStatus === "not_prepared" && preparationPercentage > 0) {
            return false;
          }
        }

        return true;
      });

      // Actualizar estado
      if (append) {
        setOrders(prev => [...prev, ...finalOrders]);
      } else {
        setOrders(finalOrders);
      }

      // Actualizar paginación
      setPagination(prev => ({
        ...prev,
        page,
        hasMore: finalOrders.length === pageSize
      }));

    } catch (error) {
      console.error('Error fetching pending orders:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los pedidos pendientes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMoreOrders = () => {
    // Si no hay paginación (filtros de cálculo aplicados), no hacer nada
    if (!pagination.hasMore) return;
    
    const nextPage = pagination.page + 1;
    fetchPendingOrders(nextPage, true);
  };

  const applyFilters = async () => {
    
    // Validar que las fechas sean coherentes
    const { from, to } = tempFilters.deliveryDateRange;
    if (from && to && from > to) {
      toast({
        title: "Error en fechas",
        description: "La fecha 'desde' no puede ser posterior a la fecha 'hasta'",
        variant: "destructive",
      });
      return;
    }
    
    setFiltersLoading(true);
    
    try {
      // Simular un pequeño delay para mostrar el estado de carga
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setAppliedFilters(tempFilters);
      
      // Resetear paginación cuando se aplican filtros
      setPagination(prev => ({ ...prev, page: 0, hasMore: true }));
      
      toast({
        title: "Filtros aplicados",
        description: "Los filtros se han aplicado correctamente",
      });
    } catch (error) {
      console.error('Error aplicando filtros:', error);
      toast({
        title: "Error",
        description: "No se pudieron aplicar los filtros",
        variant: "destructive",
      });
    } finally {
      setFiltersLoading(false);
    }
  };

  const clearFilters = async () => {
    setFiltersLoading(true);
    
    try {
      const emptyFilters = {
        customer: "",
        orderNumber: "",
        deliveryDateRange: { from: null, to: null },
        minServablePercentage: 0,
        temporada: "all",
        preparationStatus: "all",
        product: null,
      };
      setTempFilters(emptyFilters);
      setAppliedFilters(emptyFilters);
      setCustomerSearchTerm(""); // Limpiar también el término de búsqueda
      setDebouncedCustomerTerm(""); // Limpiar el término debounced
      setOrderNumberSearchTerm(""); // Limpiar el término de búsqueda del número de pedido
      setDebouncedOrderNumberTerm(""); // Limpiar el término debounced del número de pedido
      
      // Recargar todos los pedidos para resetear completamente
      await fetchAllOrders();
      
      toast({
        title: "Filtros limpiados",
        description: "Todos los filtros se han limpiado",
      });
    } catch (error) {
      console.error('Error limpiando filtros:', error);
      toast({
        title: "Error",
        description: "No se pudieron limpiar los filtros",
        variant: "destructive",
      });
    } finally {
      setFiltersLoading(false);
    }
  };

  const clearDateFilters = async () => {
    setFiltersLoading(true);
    
    try {
      // Simular un pequeño delay para mostrar el estado de carga
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setTempFilters(prev => ({ ...prev, deliveryDateRange: { from: null, to: null } }));
      setAppliedFilters(prev => ({ ...prev, deliveryDateRange: { from: null, to: null } }));
      setPagination(prev => ({ ...prev, page: 0, hasMore: true }));
      
      toast({
        title: "Filtro de fechas limpiado",
        description: "El filtro de fechas se ha limpiado",
      });
    } catch (error) {
      console.error('Error limpiando filtro de fechas:', error);
      toast({
        title: "Error",
        description: "No se pudo limpiar el filtro de fechas",
        variant: "destructive",
      });
    } finally {
      setFiltersLoading(false);
    }
  };

  const clearPreparationStatusFilter = async () => {
    setFiltersLoading(true);
    
    try {
      // Simular un pequeño delay para mostrar el estado de carga
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setTempFilters(prev => ({ ...prev, preparationStatus: "all" }));
      setAppliedFilters(prev => ({ ...prev, preparationStatus: "all" }));
      setPagination(prev => ({ ...prev, page: 0, hasMore: true }));
      
      toast({
        title: "Filtro de preparación limpiado",
        description: "El filtro de estado de preparación se ha limpiado",
      });
    } catch (error) {
      console.error('Error limpiando filtro de preparación:', error);
      toast({
        title: "Error",
        description: "No se pudo limpiar el filtro de preparación",
        variant: "destructive",
      });
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleServe = (orderId) => {
    setSelectedOrderId(orderId);
    setShowServeDialog(true);
  };

  const handlePrepare = (orderId) => {
    setSelectedOrderId(orderId);
    setShowPrepareDialog(true);
  };

  const handleShowDetails = (item) => {
    setSelectedItem(item);
    setShowDetailsDialog(true);
  };

  const handleCloseServeDialog = () => {
    setShowServeDialog(false);
    setSelectedOrderId(null);
    fetchPendingOrders(0, false);
    if (onOrderServed) {
      onOrderServed();
    }
  };

  const handleClosePrepareDialog = () => {
    setShowPrepareDialog(false);
    setSelectedOrderId(null);
    fetchPendingOrders(0, false);
  };

  const handlePreparedUpdated = () => {
    // Refrescar datos al vuelo para que los porcentajes y contadores reflejen cambios
    fetchPendingOrders(0, false);
  };

  const handleCloseDetailsDialog = () => {
    setShowDetailsDialog(false);
    setSelectedItem(null);
  };

  const hasAvailableStock = (order) => {
    const pendingItems = order.order_items?.filter(item => item.pending_quantity > 0) || [];
    if (pendingItems.length === 0) return false;
    return pendingItems.some(item => {
      const inventoryItem = item.product.inventory.find(
        inv => inv.size === item.size && inv.color === item.color
      );
      return inventoryItem && inventoryItem.quantity > 0;
    });
  };

  const calculateServablePercentage = (order) => {
    // Usar all_order_items para cálculos, pero filtrar solo pendientes para servible
    const allItems = order.all_order_items || order.order_items || [];
    const pendingItems = allItems.filter(item => item.pending_quantity > 0);
    if (pendingItems.length === 0) return 0;

    const totalPending = pendingItems.reduce((sum, item) => sum + item.pending_quantity, 0);
    const totalServable = pendingItems.reduce((sum, item) => {
      const inventoryItem = item.product.inventory.find(
        inv => inv.size === item.size && inv.color === item.color
      );
      const stockAvailable = inventoryItem?.quantity || 0;
      return sum + Math.min(stockAvailable, item.pending_quantity);
    }, 0);

    return totalPending > 0 ? (totalServable / totalPending) * 100 : 0;
  };

  const calculatePreparationPercentage = (order) => {
    // Considerar todos los items del pedido, no solo los pendientes
    const allItems = order.all_order_items || order.order_items || [];
    if (allItems.length === 0) {
      return 0;
    }

    // Calcular cantidad total original: pending + delivered
    const totalOriginalQuantity = allItems.reduce((sum, item) => {
      const pending = item.pending_quantity || 0;
      const delivered = item.delivered_quantity || 0;
      return sum + pending + delivered;
    }, 0);
    
    const totalPrepared = allItems.reduce((sum, item) => sum + (item.preparation_quantity || 0), 0);

    return totalOriginalQuantity > 0 ? (totalPrepared / totalOriginalQuantity) * 100 : 0;
  };

  const calculateServedPercentage = (order) => {
    // Usar all_order_items para cálculos (todas las líneas del pedido)
    const allItems = order.all_order_items || order.order_items || [];
    if (allItems.length === 0) return 0;

    // Calcular cantidad total original: pending + delivered
    const totalOriginalQuantity = allItems.reduce((sum, item) => {
      const pending = item.pending_quantity || 0;
      const delivered = item.delivered_quantity || 0;
      return sum + pending + delivered;
    }, 0);
    
    // Cantidad total servida
    const totalServed = allItems.reduce((sum, item) => sum + (item.delivered_quantity || 0), 0);
    


    return totalOriginalQuantity > 0 ? (totalServed / totalOriginalQuantity) * 100 : 0;
  };

  // Definir si hay filtros activos antes de usarlo
  const hasActiveFilters = appliedFilters.customer || 
    appliedFilters.orderNumber ||
    appliedFilters.deliveryDateRange.from || 
    appliedFilters.deliveryDateRange.to || 
    appliedFilters.temporada !== "all" ||
    appliedFilters.minServablePercentage > 0 || 
    appliedFilters.preparationStatus !== "all" || 
    appliedFilters.product;

  // Aplicar todos los filtros sobre los pedidos cargados usando useMemo para optimizar rendimiento
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {

    // Filtro de stock
    if (showOnlyWithStock && !hasAvailableStock(order)) {
      return false;
    }

    // Filtro por cliente
    if (appliedFilters.customer && !order.customer?.name?.toLowerCase().includes(appliedFilters.customer.toLowerCase())) {
      return false;
    }

    // Filtro por número de pedido
    if (appliedFilters.orderNumber && !order.number.toString().includes(appliedFilters.orderNumber)) {
      return false;
    }

    // Filtro por rango de fechas de entrega
    if ((appliedFilters.deliveryDateRange.from || appliedFilters.deliveryDateRange.to) && order.fecha_entrega) {
      const orderDate = new Date(order.fecha_entrega);
      const fromDate = appliedFilters.deliveryDateRange.from ? new Date(appliedFilters.deliveryDateRange.from) : null;
      const toDate = appliedFilters.deliveryDateRange.to ? new Date(appliedFilters.deliveryDateRange.to) : null;
      

      
      // Si solo hay fecha "desde", mostrar pedidos desde esa fecha (inclusive)
      if (fromDate && !toDate) {
        // Para ser inclusivo, comparamos desde el inicio del día
        const startOfDay = new Date(fromDate);
        startOfDay.setHours(0, 0, 0, 0);
        if (orderDate < startOfDay) {
          return false;
        }
      }
      
      // Si solo hay fecha "hasta", mostrar pedidos hasta esa fecha (inclusive)
      if (!fromDate && toDate) {
        // Para ser inclusivo, comparamos hasta el final del día
        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (orderDate > endOfDay) {
          return false;
        }
      }
      
      // Si hay rango completo, mostrar pedidos dentro del rango (inclusive)
      if (fromDate && toDate) {
        // Para ser inclusivo, comparamos desde el inicio del día "desde" hasta el final del día "hasta"
        const startOfDay = new Date(fromDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);
        
        if (orderDate < startOfDay || orderDate > endOfDay) {
          return false;
        }
      }
      

    }

    // Filtro por temporada en las lineas del pedido
    if (appliedFilters.temporada !== "all") {
      const selectedPreset = seasonDatePresets.find((preset) => preset.id === appliedFilters.temporada);
      if (!selectedPreset) {
        return false;
      }

      const orderDate = new Date(order.date);
      const presetRange = toDateRangeFromPreset(selectedPreset);
      if (!presetRange.from || !presetRange.to || orderDate < presetRange.from || orderDate > presetRange.to) {
        return false;
      }
    }

    // Filtro por producto
    if (appliedFilters.product) {
      const hasProduct = order.all_order_items?.some(item => item.product_id === appliedFilters.product);
      if (!hasProduct) {
        return false;
      }
    }

    // Filtro por porcentaje servible mínimo
    if (appliedFilters.minServablePercentage > 0) {
      const servablePercentage = calculateServablePercentage(order);
      if (servablePercentage < appliedFilters.minServablePercentage) {
        return false;
      }
    }

    // Filtro de estado de preparación
    if (appliedFilters.preparationStatus !== "all") {
      const preparationPercentage = calculatePreparationPercentage(order);
      
              if (appliedFilters.preparationStatus === "prepared" && preparationPercentage < 100) {
          return false;
        }
        if (appliedFilters.preparationStatus === "partial" && (preparationPercentage === 0 || preparationPercentage === 100)) {
          return false;
        }
        if (appliedFilters.preparationStatus === "not_prepared" && preparationPercentage > 0) {
          return false;
        }
    }



    return true;
  });
  }, [orders, appliedFilters, showOnlyWithStock, hasActiveFilters]); // Dependencias del useMemo



  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {loadingProgress.step || 'Cargando...'}
            </span>
            <span className="text-sm text-gray-500">
              {loadingProgress.percentage}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${loadingProgress.percentage}%` }}
            ></div>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          Cargando datos de pedidos pendientes...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        {/* Header con controles principales */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showOnlyWithStock"
                checked={showOnlyWithStock}
                onChange={(e) => setShowOnlyWithStock(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="showOnlyWithStock" className="text-sm text-gray-700">
                Solo mostrar pedidos con stock disponible
              </label>
            </div>
            <div className="text-sm text-gray-600">
              {filtersLoading ? (
                <span className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  Aplicando filtros...
                </span>
              ) : (
                <>
                  Mostrando {filteredOrders.length} de {orders.length} pedidos
                  {showOnlyWithStock && (
                    <span className="ml-2 text-blue-600">
                      (con stock disponible)
                    </span>
                  )}
                  {hasActiveFilters && (
                    <span className="ml-2 text-orange-600">
                      (filtros aplicados)
                    </span>
                  )}
                  {(appliedFilters.deliveryDateRange.from || appliedFilters.deliveryDateRange.to) && (
                    <span className="ml-2 text-purple-600">
                      (con filtro de fechas)
                    </span>
                  )}
                  {appliedFilters.preparationStatus !== "all" && (
                    <span className="ml-2 text-green-600">
                      (estado: {appliedFilters.preparationStatus === "prepared" ? "preparados" : 
                               appliedFilters.preparationStatus === "partial" ? "parcialmente preparados" : 
                               "no preparados"})
                    </span>
                  )}
                  {appliedFilters.customer && (
                    <span className="ml-2 text-cyan-600">
                      (cliente: {appliedFilters.customer})
                    </span>
                  )}
                  {appliedFilters.orderNumber && (
                    <span className="ml-2 text-cyan-600">
                      (pedido: {appliedFilters.orderNumber})
                    </span>
                  )}
                  {appliedFilters.product && (
                    <span className="ml-2 text-teal-600">
                      (producto seleccionado)
                    </span>
                  )}
                  {appliedFilters.temporada !== "all" && (
                    <span className="ml-2 text-indigo-600">
                      (temporada: {seasonDatePresets.find((preset) => preset.id === appliedFilters.temporada)?.name || appliedFilters.temporada})
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filtros
              {/* Indicador de filtros activos */}
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {Object.values(appliedFilters).filter(v => 
                    v !== "" && v !== null && v !== 0 && v !== "all"
                  ).length}
                </Badge>
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                disabled={filtersLoading}
                className="h-8 px-2"
              >
                {filtersLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-2"></div>
                    Limpiando...
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Limpiar
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Panel de filtros */}
        {showFilters && (
          <Card className="p-4 relative">
            {/* Overlay de carga */}
            {filtersLoading && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Aplicando filtros...</p>
                </div>
              </div>
            )}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium">Filtros de búsqueda</h3>
                  {/* Indicador de filtros pendientes */}
                  {JSON.stringify(tempFilters) !== JSON.stringify(appliedFilters) && (
                    <Badge variant="secondary" className="ml-2">
                      Cambios pendientes
                    </Badge>
                  )}
                  {/* Indicador de carga */}
                  {filtersLoading && (
                    <Badge variant="outline" className="ml-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                      Aplicando...
                    </Badge>
                  )}
                </div>
                <Button
                  onClick={applyFilters}
                  className="flex items-center gap-2"
                  disabled={loading || filtersLoading}
                  variant={JSON.stringify(tempFilters) !== JSON.stringify(appliedFilters) ? "default" : "outline"}
                >
                  {filtersLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Aplicando...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      Aplicar filtros
                    </>
                  )}
                </Button>
              </div>

              {/* Filtros principales */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {/* Filtro por cliente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cliente:
                  </label>
                  <Input
                    placeholder="Buscar por nombre de cliente..."
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                
                {/* Filtro por número de pedido */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de pedido:
                  </label>
                  <Input
                    placeholder="Buscar por número..."
                    value={orderNumberSearchTerm}
                    onChange={(e) => setOrderNumberSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                
                {/* Filtro por producto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Artículo:
                  </label>
                  <ProductSelector
                    onSelect={(productId) => setTempFilters(prev => ({ ...prev, product: productId }))}
                    value={tempFilters.product}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temporada:
                  </label>
                  <select
                    value={tempFilters.temporada}
                    onChange={(e) => setTempFilters(prev => ({ ...prev, temporada: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="all">Todas las temporadas</option>
                    {seasonDatePresets.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Filtro por rango de fechas de entrega */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Fecha de entrega:
                    </label>
                    {(tempFilters.deliveryDateRange.from || tempFilters.deliveryDateRange.to) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearDateFilters}
                        disabled={filtersLoading}
                        className="h-6 px-2 text-xs"
                      >
                        {filtersLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-1"></div>
                            Limpiando...
                          </>
                        ) : (
                          <>
                            <X className="h-3 w-3 mr-1" />
                            Limpiar fechas
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-[140px] justify-start text-left font-normal text-sm"
                        >
                          {tempFilters.deliveryDateRange.from ? (
                            format(tempFilters.deliveryDateRange.from, "dd/MM/yyyy", { locale: es })
                          ) : (
                            "Desde"
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={tempFilters.deliveryDateRange.from}
                          onSelect={(date) => setTempFilters(prev => ({ 
                            ...prev, 
                            deliveryDateRange: { ...prev.deliveryDateRange, from: date }
                          }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-[140px] justify-start text-left font-normal text-sm"
                        >
                          {tempFilters.deliveryDateRange.to ? (
                            format(tempFilters.deliveryDateRange.to, "dd/MM/yyyy", { locale: es })
                          ) : (
                            "Hasta"
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={tempFilters.deliveryDateRange.to}
                          onSelect={(date) => setTempFilters(prev => ({ 
                            ...prev, 
                            deliveryDateRange: { ...prev.deliveryDateRange, to: date }
                          }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
              
              {/* Filtros secundarios */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <label htmlFor="minPercentage" className="text-sm text-gray-700 whitespace-nowrap">
                    Mín. % servible:
                  </label>
                  <Input
                    id="minPercentage"
                    type="number"
                    min="0"
                    max="100"
                    value={tempFilters.minServablePercentage}
                    onChange={(e) => setTempFilters(prev => ({ 
                      ...prev, 
                      minServablePercentage: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                    }))}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <label htmlFor="preparationStatus" className="text-sm text-gray-700 whitespace-nowrap">
                      Estado preparación:
                    </label>
                    <select
                      id="preparationStatus"
                      value={tempFilters.preparationStatus}
                      onChange={(e) => setTempFilters(prev => ({ ...prev, preparationStatus: e.target.value }))}
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="all">Todos los estados</option>
                      <option value="prepared">Preparados</option>
                      <option value="partial">Parcialmente preparados</option>
                      <option value="not_prepared">No preparados</option>
                    </select>
                    {tempFilters.preparationStatus !== "all" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearPreparationStatusFilter}
                        disabled={filtersLoading}
                        className="h-6 px-2 text-xs"
                      >
                        {filtersLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-1"></div>
                            Limpiando...
                          </>
                        ) : (
                          <>
                            <X className="h-3 w-3 mr-1" />
                            Limpiar
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {filteredOrders.map((order) => {
        const pendingItems = order.order_items?.filter(item => item.pending_quantity > 0) || [];
        const hasStock = hasAvailableStock(order);
        const servablePercentage = calculateServablePercentage(order);
        const preparationPercentage = calculatePreparationPercentage(order);
        const servedPercentage = calculateServedPercentage(order);

        return (
          <Card key={order.id} className={`mb-4 ${!hasStock && showOnlyWithStock ? 'hidden' : ''}`}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <CardTitle className="text-lg">
                    Pedido #{order.number}
                  </CardTitle>
                  <div className="text-sm text-gray-600">
                    {new Date(order.date).toLocaleDateString()}
                  </div>
                  <div className="text-sm font-medium text-blue-600">
                    {order.customer?.name}
                  </div>
                  {order.fecha_entrega && (
                    <div className="text-sm text-gray-500">
                      Entrega: {new Date(order.fecha_entrega).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrepare(order.id)}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Preparar
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleServe(order.id)}
                    disabled={!hasStock || preparationPercentage === 0}
                  >
                    <Truck className="h-4 w-4 mr-2" />
                    Servir
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Resumen de porcentajes */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Servible:</span>
                    <span className={`text-sm font-bold ${servablePercentage >= 100 ? 'text-green-600' : servablePercentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {servablePercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Preparado:</span>
                    <span className={`text-sm font-bold ${preparationPercentage >= 100 ? 'text-green-600' : preparationPercentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {preparationPercentage.toFixed(1)}%
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Servido:</span>
                    <span className={`text-sm font-bold ${servedPercentage >= 100 ? 'text-green-600' : servedPercentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {servedPercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Total:</span>
                    <span className="text-sm font-bold"> €{order.total?.toFixed(2)}</span>
                  </div>
                </div>

                {/* Items pendientes compactos */}
                <div className="lg:col-span-2">
                  <div className="text-sm font-medium mb-2">Items pendientes ({pendingItems.length}):</div>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {pendingItems.map((item) => {
                      const inventoryItem = item.product.inventory.find(
                        inv => inv.size === item.size && inv.color === item.color
                      );
                      const stockAvailable = inventoryItem?.quantity || 0;
                      const hasStockIssue = stockAvailable < item.pending_quantity;

                      return (
                        <div key={item.id} className={`flex items-center justify-between p-2 rounded border ${hasStockIssue ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                          <div className="flex items-center gap-3 flex-1">
                            {item.product.images?.[item.product.main_image_index] && (
                              <img
                                src={item.product.images[item.product.main_image_index]}
                                alt={item.product.description}
                                className="w-8 h-8 object-cover rounded"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {item.product.code} - {item.product.description}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <span>{item.size}</span>
                                <ColorBadge color={item.color} />
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right text-xs">
                              <div className="font-medium">Pendiente: {item.pending_quantity}</div>
                              <div className="text-gray-500">Stock: {stockAvailable}</div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleShowDetails(item)}
                              className="h-8 w-8 p-0"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {pagination.hasMore && (
        <div className="flex justify-center mt-6">
          <Button
            onClick={loadMoreOrders}
            variant="outline"
            disabled={loading}
          >
            {loading ? "Cargando..." : "Cargar más pedidos"}
          </Button>
        </div>
      )}

      <Dialog open={showServeDialog} onOpenChange={setShowServeDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Servir Pedido</DialogTitle>
          </DialogHeader>
          {selectedOrderId && (
            <ServeOrderForm
              orderId={selectedOrderId}
              onClose={handleCloseServeDialog}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPrepareDialog} onOpenChange={(open) => {
        setShowPrepareDialog(open);
        if (!open) {
          // Al cerrar por cualquier vía, refrescar
          fetchPendingOrders(0, false);
        }
      }}>
        <DialogContent className="max-w-[90vw] w-[90vw]">
          <DialogHeader>
            <DialogTitle>Preparar Pedido</DialogTitle>
          </DialogHeader>
          {selectedOrderId && (
            <PrepareOrderForm
              orderId={selectedOrderId}
              onClose={handleClosePrepareDialog}
              onUpdated={handlePreparedUpdated}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalles del Item</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                {selectedItem.product.images?.[selectedItem.product.main_image_index] && (
                  <img
                    src={selectedItem.product.images[selectedItem.product.main_image_index]}
                    alt={selectedItem.product.description}
                    className="w-24 h-24 object-cover rounded"
                  />
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">
                    {selectedItem.product.code} - {selectedItem.product.description}
                  </h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-gray-600">Talla: {selectedItem.size}</span>
                    <ColorBadge color={selectedItem.color} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Cantidades:</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Pedido original:</span>
                      <span className="font-medium">{selectedItem.quantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pendiente:</span>
                      <span className="font-medium text-orange-600">{selectedItem.pending_quantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Preparado:</span>
                      <span className="font-medium text-blue-600">{selectedItem.preparation_quantity || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Servido:</span>
                      <span className="font-medium text-green-600">{selectedItem.delivered_quantity || 0}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Stock disponible:</h4>
                  <div className="space-y-1 text-sm">
                    {(() => {
                      const inventoryItem = selectedItem.product.inventory.find(
                        inv => inv.size === selectedItem.size && inv.color === selectedItem.color
                      );
                      const stockAvailable = inventoryItem?.quantity || 0;
                      const hasStockIssue = stockAvailable < selectedItem.pending_quantity;
                      
                      return (
                        <>
                          <div className="flex justify-between">
                            <span>Stock actual:</span>
                            <span className={`font-medium ${hasStockIssue ? 'text-red-600' : 'text-green-600'}`}>
                              {stockAvailable}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Necesario:</span>
                            <span className="font-medium">{selectedItem.pending_quantity}</span>
                          </div>
                          {hasStockIssue && (
                            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                              ⚠️ Stock insuficiente. Faltan {selectedItem.pending_quantity - stockAvailable} unidades.
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleCloseDetailsDialog}
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
